import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me, type NotificationRow, type AlertRule } from '../lib/api';

function timeAgo(iso: string) {
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function sevDot(sev: NotificationRow['severity']) {
  const colour = sev === 'critical' || sev === 'error' ? 'bg-tnh-err'
               : sev === 'warn' ? 'bg-tnh-warn'
               : 'bg-gray-400 dark:bg-gray-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${colour}`} title={sev} />;
}

export default function Notifications({ me }: { me: Me }) {
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('unread');
  const [list, setList] = useState<NotificationRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setErr(null); const r = await api.notificationsList(filter, 100); setList(r.notifications); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [filter]);

  async function mark(n: NotificationRow, action: 'read' | 'dismiss') {
    try {
      await api.notificationsMark(n.notification_id, action);
      setList((cur) => cur?.map((x) => x.notification_id === n.notification_id
        ? { ...x, ...(action === 'read' ? { read_at: new Date().toISOString() } : { dismissed_at: new Date().toISOString() }) }
        : x).filter((x) => action === 'dismiss' ? x.notification_id !== n.notification_id : true) ?? null);
    } catch (e: any) { setErr(e.message); }
  }

  async function sendTest() {
    setBusy(true);
    try { await api.notificationsTest(); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function markAllRead() {
    if (!list) return;
    setBusy(true);
    try {
      await Promise.all(list.filter((n) => !n.read_at).map((n) => api.notificationsMark(n.notification_id, 'read')));
      await load();
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-gray-500 text-sm mt-1">
            Signals from across the platform. The notification-svc consumes them from the queue, persists here, and
            sends an email when severity warrants it.
          </p>
        </div>
        <div className="flex gap-2">
          {me.role === 'super_admin' && (
            <button onClick={sendTest} disabled={busy} className="btn-ghost text-sm">Send test</button>
          )}
          <button onClick={markAllRead} disabled={busy || !list || list.length === 0} className="btn-ghost text-sm">Mark all read</button>
        </div>
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      <div className="flex items-center gap-2">
        {(['unread', 'all', 'read'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-md text-xs ${
                    filter === f ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                  }`}>
            {f}
          </button>
        ))}
      </div>

      {list === null && <div className="card p-8 text-center text-gray-500">Loading…</div>}
      {list && list.length === 0 && (
        <div className="card p-8 text-center text-gray-500">
          {filter === 'unread' ? 'All caught up — no unread notifications.' : 'Nothing here yet.'}
        </div>
      )}
      <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
        {list?.map((n) => (
          <div key={n.notification_id} className={`px-4 py-3 ${!n.read_at ? 'bg-tnh-accent/[0.03]' : ''}`}>
            <div className="flex items-start gap-3">
              <div className="pt-1.5">{sevDot(n.severity)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{n.kind}</span>
                  <span className="text-xs text-gray-500">{timeAgo(n.created_at)}</span>
                  {n.email_sent === 1 && <span className="text-[10px] text-gray-400">email sent</span>}
                  {!n.read_at && <span className="text-[10px] text-tnh-accent font-semibold ml-auto">NEW</span>}
                </div>
                <div className="font-medium mt-1">{n.title}</div>
                <div className="text-sm text-gray-500 mt-1">{n.body}</div>
                <div className="flex gap-3 mt-2 text-xs">
                  {n.link_url && (
                    <Link to={n.link_url.startsWith('http') ? '#' : n.link_url}
                          onClick={() => mark(n, 'read')}
                          className="text-tnh-accent hover:underline">
                      Open →
                    </Link>
                  )}
                  {!n.read_at && (
                    <button onClick={() => mark(n, 'read')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                      Mark read
                    </button>
                  )}
                  <button onClick={() => mark(n, 'dismiss')} className="text-gray-500 hover:text-tnh-err">
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AlertRulesPanel me={me} />
    </div>
  );
}

const RULE_TYPES: { v: AlertRule['type']; label: string }[] = [
  { v: 'tenant_total', label: 'Tenant total volume' },
  { v: 'outbound', label: 'Outbound calls' },
  { v: 'queue', label: 'Specific queue' },
  { v: 'all_queues', label: 'Any queue (all)' },
  { v: 'queue_ratio', label: 'Queue calls-vs-agents (KPI impact)' },
  { v: 'emergency', label: 'Emergency number (911/9911)' },
];

function AlertRulesPanel({ me }: { me: Me }) {
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<any | null>(null); // null = closed; object = add/edit
  const [queues, setQueues] = useState<string[]>([]);
  const canEdit = me.role === 'super_admin' || me.role === 'tenant_admin';

  async function load() {
    try { setErr(null); const r = await api.alertRulesList(); setRules(r.rules); api.wxccQueues().then((q) => setQueues((q.queues || []).map((x: any) => x.name).filter(Boolean).sort())).catch(() => {}); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  function openAdd() {
    setForm({ name: '', type: 'tenant_total', threshold: 30, window_minutes: 15, queue_name: '', match_numbers: '911, 9911', recipients: '', severity: 'warn', enabled: true, agents: 5, ratio: 10, eob_enabled: false, business_end: '17:00', tz_offset_min: -420 });
  }
  function openEdit(r: AlertRule) {
    let recips: string[] = []; try { recips = JSON.parse(r.recipients_json || '[]'); } catch {}
    let nums: string[] = []; try { nums = JSON.parse(r.match_numbers_json || '[]'); } catch {}
    let cfg: any = {}; try { cfg = JSON.parse((r as any).config_json || '{}'); } catch {}
    setForm({ rule_id: r.rule_id, name: r.name, type: r.type, threshold: r.threshold, window_minutes: r.window_minutes,
      queue_name: r.queue_name || '', match_numbers: nums.join(', '), recipients: recips.join(', '), severity: r.severity, enabled: r.enabled === 1,
      agents: (r as any).agents ?? 5, ratio: cfg.ratio ?? r.threshold ?? 10, eob_enabled: !!cfg.eob_enabled, business_end: cfg.business_end || '17:00', tz_offset_min: cfg.tz_offset_min ?? -420 });
  }

  async function save() {
    if (!form) return;
    setBusy(true); setErr(null);
    try {
      const payload: any = {
        name: form.name || 'Alert rule', type: form.type,
        threshold: Number(form.threshold) || (form.type === 'emergency' ? 1 : 30),
        window_minutes: Number(form.window_minutes) || (form.type === 'emergency' ? 5 : 15),
        severity: form.type === 'emergency' ? 'critical' : form.severity,
        enabled: !!form.enabled,
        recipients: String(form.recipients || '').split(',').map((x: string) => x.trim()).filter(Boolean),
      };
      if (form.type === 'queue' || form.type === 'queue_ratio') payload.queue_name = form.queue_name;
      if (form.type === 'queue_ratio') {
        payload.agents = Number(form.agents) || 0;
        payload.threshold = Number(form.ratio) || 10;
        payload.config = { ratio: Number(form.ratio) || 10, eob_enabled: !!form.eob_enabled, business_end: form.business_end || '17:00', tz_offset_min: Number(form.tz_offset_min) || -420 };
      }
      if (form.type === 'emergency') payload.match_numbers = String(form.match_numbers || '').split(',').map((x: string) => x.trim()).filter(Boolean);
      await api.alertRuleSave(payload, form.rule_id);
      setForm(null); await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function toggle(r: AlertRule) {
    try { await api.alertRuleSave({ enabled: r.enabled !== 1 }, r.rule_id); await load(); } catch (e: any) { setErr(e.message); }
  }
  async function remove(r: AlertRule) {
    if (!confirm(`Delete alert rule "${r.name}"?`)) return;
    try { await api.alertRuleDelete(r.rule_id); await load(); } catch (e: any) { setErr(e.message); }
  }
  async function test(r: AlertRule) {
    try { const res = await api.alertRuleTest(r.rule_id); alert(`Test alert sent to: ${(res.recipients || []).join(', ') || '(no recipients set)'}`); }
    catch (e: any) { setErr(e.message); }
  }

  function summarize(r: AlertRule): string {
    if (r.type === 'emergency') { let n: string[] = []; try { n = JSON.parse(r.match_numbers_json || '[]'); } catch {} return `dialed ${n.join(' / ') || '911'}`; }
    const w = `${r.window_minutes} min`;
    if (r.type === 'queue_ratio') { let c: any = {}; try { c = JSON.parse((r as any).config_json || '{}'); } catch {} return `≥${c.ratio ?? r.threshold}:1 calls/agent · ${(r as any).agents ?? 0} agents · queue "${r.queue_name}"${c.eob_enabled ? ' · +EOB ' + (c.business_end || '') : ''}`; }
    if (r.type === 'queue') return `≥${r.threshold} in ${w} · queue "${r.queue_name}"`;
    if (r.type === 'all_queues') return `≥${r.threshold} in ${w} · any queue`;
    if (r.type === 'outbound') return `≥${r.threshold} outbound in ${w}`;
    return `≥${r.threshold} calls in ${w}`;
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">Call-volume & emergency alerts</h2>
          <p className="text-xs text-gray-500 mt-0.5">Email from notify@trinethub.com when a threshold is crossed or 911/9911 is dialed. Evaluated every minute.</p>
        </div>
        {canEdit && <button onClick={openAdd} className="btn-primary text-xs">+ Add rule</button>}
      </div>
      {err && <div className="text-xs text-tnh-err mb-2">{err}</div>}
      {rules === null && <div className="text-sm text-gray-500">Loading…</div>}
      {rules && rules.length === 0 && <div className="text-sm text-gray-500">No alert rules yet.</div>}
      <div className="space-y-2">
        {rules?.map((r) => {
          let recips: string[] = []; try { recips = JSON.parse(r.recipients_json || '[]'); } catch {}
          return (
            <div key={r.rule_id} className="border border-tnh-border dark:border-tnh-border-dark rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={r.type === 'emergency' ? 'pill-err' : 'pill-warn'}>{r.type === 'emergency' ? 'EMERGENCY' : (RULE_TYPES.find((t) => t.v === r.type)?.label ?? r.type)}</span>
                <span className="font-medium">{r.name}</span>
                <span className="text-gray-500">— {summarize(r)}</span>
                <span className={`ml-auto text-[10px] uppercase ${r.enabled === 1 ? 'text-tnh-ok' : 'text-gray-400'}`}>{r.enabled === 1 ? 'enabled' : 'disabled'}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">to: {recips.join(', ') || '(no recipients)'}</div>
              {canEdit && (
                <div className="flex gap-3 mt-2 text-xs">
                  <button onClick={() => openEdit(r)} className="text-tnh-accent hover:underline">Edit</button>
                  <button onClick={() => test(r)} className="text-gray-500 hover:underline">Test</button>
                  <button onClick={() => toggle(r)} className="text-gray-500 hover:underline">{r.enabled === 1 ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => remove(r)} className="text-tnh-err hover:underline">Delete</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {form && (
        <div className="mt-4 border-t border-tnh-border dark:border-tnh-border-dark pt-4 space-y-2">
          <div className="text-xs font-semibold">{form.rule_id ? 'Edit rule' : 'New rule'}</div>
          <div className="grid gap-2 md:grid-cols-2">
            <input className="input-sm" placeholder="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select className="input-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {RULE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            {form.type !== 'emergency' && form.type !== 'queue_ratio' && <input className="input-sm" type="number" placeholder="Threshold (calls)" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} />}
            {form.type !== 'emergency' && <input className="input-sm" type="number" placeholder="Window (minutes)" value={form.window_minutes} onChange={(e) => setForm({ ...form, window_minutes: e.target.value })} />}
            {(form.type === 'queue' || form.type === 'queue_ratio') && (queues.length > 0
              ? <select className="input-sm" value={form.queue_name} onChange={(e) => setForm({ ...form, queue_name: e.target.value })}>
                  <option value="">— select queue —</option>
                  {queues.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              : <input className="input-sm" placeholder="Queue name (exact)" value={form.queue_name} onChange={(e) => setForm({ ...form, queue_name: e.target.value })} />)}
            {form.type === 'emergency' && <input className="input-sm" placeholder="Numbers (comma-sep)" value={form.match_numbers} onChange={(e) => setForm({ ...form, match_numbers: e.target.value })} />}
            {form.type === 'queue_ratio' && <input className="input-sm" type="number" placeholder="Agents staffed" value={form.agents} onChange={(e) => setForm({ ...form, agents: e.target.value })} />}
            {form.type === 'queue_ratio' && <input className="input-sm" type="number" placeholder="Ratio (calls per agent)" value={form.ratio} onChange={(e) => setForm({ ...form, ratio: e.target.value })} />}
          </div>
          {form.type === 'queue_ratio' && (
            <label className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <input type="checkbox" checked={!!form.eob_enabled} onChange={(e) => setForm({ ...form, eob_enabled: e.target.checked })} />
              Also alert on calls in the final minute of business hours, ending at
              <input className="input-sm" style={{ width: 90 }} placeholder="17:00" value={form.business_end} onChange={(e) => setForm({ ...form, business_end: e.target.value })} />
            </label>
          )}
          <input className="input-sm w-full" placeholder="Recipient emails (comma-separated)" value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="btn-ghost text-xs">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary text-xs">{busy ? 'Saving…' : 'Save rule'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
